/**
 * Canvas plumbing for logo ingest.
 *
 * All the decisions live in `core/logo.ts` and are unit-tested there; this file only
 * moves pixels in and out of a canvas, which is the part that needs a DOM and cannot be
 * tested without one (AD-11).
 */

import {
  contentBounds,
  fitWithin,
  logoPlan,
  removeBackground,
  squareCropRect,
  type LogoKind,
} from '../../core/logo';

export interface ProcessedLogo {
  blob: Blob;
  /** Object URL for preview. The caller revokes it. */
  url: string;
  width: number;
  height: number;
  /** What the ingest actually did, so the UI can say so rather than claim it. */
  notes: string[];
}

const MAX_INPUT_BYTES = 10 * 1024 * 1024; // matches the Storage rule (FR-ATT-02)

export class LogoError extends Error {}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new LogoError('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

function context(width: number, height: number): CanvasRenderingContext2D {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new LogoError('Canvas is unavailable in this browser.');
  return ctx;
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new LogoError('Could not encode the PNG.'))),
      'image/png',
    );
  });
}

/**
 * Normalise an uploaded logo to a transparent PNG at a sensible size (FR-SET-03a).
 *
 * Always stores PNG, whatever came in: JPEG has no alpha, so a JPEG round-trip would
 * silently reinstate the background this function just removed.
 */
export async function processLogo(file: File, kind: LogoKind): Promise<ProcessedLogo> {
  if (file.size > MAX_INPUT_BYTES) {
    throw new LogoError('That file is over 10 MB. Storage rules reject it.');
  }
  if (!file.type.startsWith('image/')) {
    throw new LogoError('Only image files can be used as a logo.');
  }

  const plan = logoPlan(kind);
  const img = await loadImage(file);
  const notes: string[] = [];

  // 1 · crop
  const source = { width: img.naturalWidth, height: img.naturalHeight };
  const crop = plan.square ? squareCropRect(source) : { x: 0, y: 0, ...source };
  if (plan.square && (source.width !== source.height)) {
    notes.push(`Cropped ${source.width}x${source.height} to a ${crop.width}px square`);
  }

  let ctx = context(crop.width, crop.height);
  ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

  // 2 · background
  if (plan.stripBackground) {
    const image = ctx.getImageData(0, 0, crop.width, crop.height);
    const cleared = removeBackground(image.data, { width: crop.width, height: crop.height });
    ctx.putImageData(image, 0, 0);

    const percent = Math.round((cleared / (crop.width * crop.height)) * 100);
    notes.push(
      cleared > 0
        ? `Removed the background (${percent}% of the image)`
        : 'No uniform background found — left as-is',
    );

    // 3 · trim the transparent margin, or the mark scales into the header far smaller
    // than the space allotted to it.
    const trimmed = contentBounds(image.data, { width: crop.width, height: crop.height }, 4);
    if (trimmed.width < crop.width || trimmed.height < crop.height) {
      const next = context(trimmed.width, trimmed.height);
      next.drawImage(
        ctx.canvas,
        trimmed.x, trimmed.y, trimmed.width, trimmed.height,
        0, 0, trimmed.width, trimmed.height,
      );
      ctx = next;
      notes.push(`Trimmed to content, ${trimmed.width}x${trimmed.height}`);
    }
  }

  // 4 · scale
  const fitted = fitWithin({ width: ctx.canvas.width, height: ctx.canvas.height }, plan.maxEdge);
  if (fitted.width !== ctx.canvas.width) {
    const next = context(fitted.width, fitted.height);
    next.imageSmoothingQuality = 'high';
    next.drawImage(ctx.canvas, 0, 0, fitted.width, fitted.height);
    ctx = next;
    notes.push(`Scaled to ${fitted.width}x${fitted.height}`);
  }

  const blob = await toBlob(ctx.canvas);
  return {
    blob,
    url: URL.createObjectURL(blob),
    width: ctx.canvas.width,
    height: ctx.canvas.height,
    notes,
  };
}
